/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Mail, Shield, Trash2, CalendarClock, Send, Eye, Plus, X as XIcon } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth, formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function FirmSettings() {
    const { tenant, refreshMe, user } = useAuth();
    const [form, setForm] = useState({
        name: "", country: "NZ", primary_color: "#171717", default_reviewer_user_id: "",
        practice_manager_url: "", practice_manager_label: "",
        prosaic_referral_url: "", xero_advisor_email: "",
    });
    const [labels, setLabels] = useState(null);
    const [staff, setStaff] = useState([]);
    const [busy, setBusy] = useState(false);
    const readOnly = user?.role !== "firm_admin";

    useEffect(() => {
        if (tenant) {
            setForm({
                name: tenant.name || "",
                country: tenant.country || "NZ",
                primary_color: tenant.branding?.primary_color || "#171717",
                default_reviewer_user_id: tenant.default_reviewer_user_id || "",
                practice_manager_url: tenant.practice_manager_url || "",
                practice_manager_label: tenant.practice_manager_label || "",
                prosaic_referral_url: tenant.prosaic_referral_url || "",
                xero_advisor_email: tenant.xero_advisor_email || "",
            });
        }
        api.get("/tenant/labels").then((r) => setLabels(r.data)).catch(() => {});
        api.get("/auth/users")
            .then((r) => setStaff(r.data.filter((u) => u.role === "firm_admin" || u.role === "firm_staff")))
            .catch(() => {});
    }, [tenant]);

    async function save() {
        if (readOnly) return;
        setBusy(true);
        try {
            await api.patch("/tenant/", {
                name: form.name,
                country: form.country,
                branding: { primary_color: form.primary_color },
                default_reviewer_user_id: form.default_reviewer_user_id || null,
                practice_manager_url: form.practice_manager_url || null,
                practice_manager_label: form.practice_manager_label || null,
                prosaic_referral_url: form.prosaic_referral_url || null,
                xero_advisor_email: form.xero_advisor_email || null,
            });
            toast.success("Firm settings saved");
            await refreshMe();
            const { data } = await api.get("/tenant/labels");
            setLabels(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div>
            <PageHeader
                eyebrow="Settings"
                title="Firm settings"
                description="Branding and feature toggles for your firm."
                testId="settings-header"
            />

            <div className="mb-4 flex flex-wrap gap-2">
                <a
                    href="/app/settings/email-templates"
                    className="inline-flex items-center gap-2 rounded-full border border-[#E4E4E7] bg-white px-3 py-1.5 text-sm text-[#0F1115] hover:bg-zinc-50"
                    data-testid="settings-email-templates-link"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z"/><path d="m22 6-10 7L2 6"/></svg>
                    Manage email templates →
                </a>
                <a
                    href="/app/settings/sign-in-helper"
                    className="inline-flex items-center gap-2 rounded-full border border-[#E4E4E7] bg-white px-3 py-1.5 text-sm text-[#0F1115] hover:bg-zinc-50"
                    data-testid="settings-sign-in-helper-link"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="16" r="1"/><rect x="3" y="10" width="18" height="12" rx="2"/><path d="M7 10V7a5 5 0 0 1 10 0v3"/></svg>
                    Sign-in helper (OTP) →
                </a>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <div className="co-card co-card-pad">
                    <div className="co-label">Firm profile</div>
                    <div className="mt-4 space-y-3">
                        <div><Label>Firm name</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} disabled={readOnly} data-testid="settings-name" /></div>
                        <div>
                            <Label>Primary brand colour</Label>
                            <Input type="text" className="mt-1 font-mono" value={form.primary_color} onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))} disabled={readOnly} data-testid="settings-color" />
                        </div>
                        <div>
                            <Label>Default submission reviewer</Label>
                            <Select
                                value={form.default_reviewer_user_id || "__none__"}
                                onValueChange={(v) => setForm((f) => ({ ...f, default_reviewer_user_id: v === "__none__" ? "" : v }))}
                                disabled={readOnly}
                            >
                                <SelectTrigger className="mt-1" data-testid="settings-default-reviewer"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">Auto — first firm admin</SelectItem>
                                    {staff.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.name} ({u.role.replace("_", " ")})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="mt-1.5 text-[11px] text-[#5E636E]">
                                Every new document submission auto-creates a review task. Requests with an assigned staffer override this default.
                            </p>
                        </div>
                        <div className="border-t border-[#E4E4E7] pt-4 mt-2">
                            <Label>Practice Manager link</Label>
                            <p className="text-[11px] text-[#5E636E] mt-1">
                                The URL of your existing Practice Manager / workflow system. Shown as a one-click link in the sidebar and next to every imported job &amp; client.
                            </p>
                            <Input
                                className="mt-2 font-mono"
                                placeholder="https://your-practice-manager.example.com"
                                value={form.practice_manager_url}
                                onChange={(e) => setForm((f) => ({ ...f, practice_manager_url: e.target.value }))}
                                disabled={readOnly}
                                data-testid="settings-pm-url"
                            />
                            <Input
                                className="mt-2"
                                placeholder="Display label (e.g. NZTaxation Practice Manager)"
                                value={form.practice_manager_label}
                                onChange={(e) => setForm((f) => ({ ...f, practice_manager_label: e.target.value }))}
                                disabled={readOnly}
                                data-testid="settings-pm-label"
                            />
                        </div>
                        <div className="border-t border-[#E4E4E7] pt-4 mt-2">
                            <Label>Data-source integrations</Label>
                            <p className="text-[11px] text-[#5E636E] mt-1">
                                Shown to clients on first login when their <em>Data source</em> is set to Prosaic, Xero, or Both. Set once — every flagged client sees these.
                            </p>
                            <div className="mt-2 space-y-2">
                                <div>
                                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Prosaic referral URL</Label>
                                    <Input
                                        className="mt-1 font-mono"
                                        placeholder="https://prosaic.app/connect?ref=…"
                                        value={form.prosaic_referral_url}
                                        onChange={(e) => setForm((f) => ({ ...f, prosaic_referral_url: e.target.value }))}
                                        disabled={readOnly}
                                        data-testid="settings-prosaic-url"
                                    />
                                </div>
                                <div>
                                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Xero advisor email</Label>
                                    <Input
                                        className="mt-1"
                                        type="email"
                                        placeholder="info@nztaxation.nz"
                                        value={form.xero_advisor_email}
                                        onChange={(e) => setForm((f) => ({ ...f, xero_advisor_email: e.target.value }))}
                                        disabled={readOnly}
                                        data-testid="settings-xero-email"
                                    />
                                    <p className="mt-1 text-[10px] text-[#A4A6AB]">Clients add this email as an Adviser in their Xero. Leave blank to use your owner email.</p>
                                </div>
                            </div>
                        </div>
                        <div className="pt-2">
                            <Button onClick={save} disabled={busy || readOnly} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="settings-save">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                            </Button>
                            {readOnly && <span className="ml-3 text-xs text-[#5E636E]">Firm admin only.</span>}
                        </div>
                    </div>
                </div>

                <div className="co-card co-card-pad">
                    <div className="co-label">Country-aware terminology</div>
                    <p className="mt-2 text-sm text-[#5E636E]">These labels surface throughout the client portal and request forms.</p>
                    {labels && (
                        <dl className="mt-4 grid grid-cols-2 gap-y-2 text-sm">
                            <Row k="Tax authority" v={labels.labels.tax_authority_full} />
                            <Row k="Tax portal" v={labels.labels.tax_portal} />
                            <Row k="Consumption tax" v={labels.labels.consumption_tax} />
                            <Row k="Payroll tax" v={labels.labels.payroll_tax} />
                            <Row k="ID number" v={labels.labels.id_number_label} />
                            <Row k="Retirement scheme" v={labels.labels.retirement_scheme} />
                            <Row k="Return types" v={(labels.labels.return_examples || []).join(", ")} />
                        </dl>
                    )}
                </div>

                <ChangePasswordCard />

                <TeamCard />

                <PositionsCard />

                <StaffDefaultsCard />

                <WeeklyUsageReportCard />
            </div>
        </div>
    );
}

function TeamCard() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [staff, setStaff] = useState([]);
    const [positions, setPositions] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", role: "firm_staff", password: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(null); // staff row or null

    async function load() {
        try {
            const [{ data: users }, { data: pos }] = await Promise.all([
                api.get("/auth/users"),
                api.get("/positions"),
            ]);
            setStaff(users.filter((u) => u.role === "firm_admin" || u.role === "firm_staff"));
            setPositions(pos || []);
        } catch { /* ignore */ }
    }
    useEffect(() => { load(); }, []);

    async function assignPosition(userId, positionId) {
        try {
            await api.put(`/positions/users/${userId}`, { position_id: positionId || null });
            toast.success("Position updated");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    function genPassword() {
        const arr = new Uint8Array(12);
        crypto.getRandomValues(arr);
        const s = btoa(String.fromCharCode(...arr)).replace(/[+/=]/g, "").slice(0, 12);
        setForm((f) => ({ ...f, password: s + "!" }));
    }

    async function submit(e, opts = {}) {
        e?.preventDefault();
        setErr(null);
        if (form.password.length < 8) { setErr("Temporary password must be at least 8 characters"); return; }
        setBusy(true);
        try {
            await api.post("/auth/invite", {
                email: form.email.trim().toLowerCase(),
                name: form.name.trim(),
                role: form.role,
                password: form.password,
                convert_client: !!opts.convertClient,
            });
            toast.success(opts.convertClient
                ? `${form.name} converted from client to staff — share the new temp password.`
                : `${form.name} added — share the temp password securely. They can change it in Settings.`);
            setOpen(false);
            setForm({ name: "", email: "", role: "firm_staff", password: "" });
            load();
        } catch (e) {
            const msg = formatApiError(e);
            // Sniff out the "same-tenant client collision" message and expose
            // a one-click convert button.
            if (typeof msg === "string" && msg.includes("convert_client=true")) {
                setErr({ text: msg, canConvert: true });
            } else {
                setErr(msg);
            }
        }
        finally { setBusy(false); }
    }

    async function removeStaff(row) {
        setRemovingId(row.id);
        try {
            await api.delete(`/auth/users/${row.id}`);
            toast.success(`${row.name || row.email} removed`);
            setConfirmRemove(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRemovingId(null);
        }
    }

    return (
        <div className="co-card co-card-pad lg:col-span-2" data-testid="team-card">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="co-label">Team</div>
                    <h3 className="mt-1 font-display text-lg">Staff with firm access</h3>
                    <p className="mt-1 text-sm text-[#5E636E]">
                        Firm admins can create both admins and staff. Staff can review documents and manage clients but can&apos;t change firm settings.
                    </p>
                </div>
                {isAdmin && (
                    <Button
                        onClick={() => { setOpen(true); genPassword(); }}
                        className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white shrink-0"
                        data-testid="team-add-btn"
                    >
                        <UserPlus className="h-4 w-4" /> Add staff
                    </Button>
                )}
            </div>

            <ul className="mt-5 divide-y divide-[#E4E4E7] border border-[#E4E4E7] rounded-lg overflow-hidden" data-testid="team-list">
                {staff.length === 0 && <li className="px-4 py-6 text-sm text-[#5E636E]">No staff yet.</li>}
                {staff.map((m) => (
                    <li key={m.id} className="flex items-center gap-3 px-4 py-3" data-testid={`team-row-${m.id}`}>
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#F5F4EF] text-[#0E3B2E] text-xs font-medium">
                            {(m.name || m.email).slice(0, 2).toUpperCase()}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{m.name}</div>
                            <div className="text-xs text-[#5E636E] truncate">{m.email}</div>
                        </div>
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                            m.role === "firm_admin" ? "border-[#0E3B2E]/30 bg-[#0E3B2E]/10 text-[#0E3B2E]" : "border-[#E4E4E7] bg-white text-[#5E636E]"
                        }`}>
                            {m.role === "firm_admin" ? <><Shield className="h-3 w-3" /> Admin</> : "Staff"}
                        </span>
                        {m.role === "firm_staff" && (
                            isAdmin ? (
                                <select
                                    value={m.position_id || ""}
                                    onChange={(e) => assignPosition(m.id, e.target.value)}
                                    className="ml-1 h-7 text-[11px] rounded-md border border-[#E4E4E7] bg-white px-1.5 max-w-[160px]"
                                    data-testid={`team-position-${m.id}`}>
                                    <option value="">— No position —</option>
                                    {positions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            ) : (
                                m.position_id && (
                                    <span className="ml-1 inline-flex items-center rounded-full border border-[#E4E4E7] bg-zinc-50 text-[#5E636E] text-[10px] font-medium px-2 py-0.5">
                                        {positions.find((p) => p.id === m.position_id)?.name || "—"}
                                    </span>
                                )
                            )
                        )}
                        {isAdmin && m.id !== user?.id && (
                            <button
                                type="button"
                                onClick={() => setConfirmRemove(m)}
                                className="ml-1 grid h-8 w-8 shrink-0 place-items-center rounded-full text-[#5E636E] hover:bg-rose-50 hover:text-rose-600 transition-colors"
                                title={`Remove ${m.name || m.email}`}
                                data-testid={`team-remove-btn-${m.id}`}
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        )}
                    </li>
                ))}
            </ul>

            {!isAdmin && (
                <p className="mt-3 text-[11px] text-[#5E636E]">Only firm admins can add or remove staff.</p>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="team-add-dialog">
                    <DialogHeader>
                        <DialogTitle>Add a staff member</DialogTitle>
                        <DialogDescription>
                            They&apos;ll sign in at the same /login page using the temporary password below. They can change it themselves in Settings → Security.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-3" data-testid="team-add-form">
                        <div>
                            <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Full name</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                placeholder="Jane Doe"
                                className="mt-1.5"
                                data-testid="team-name"
                                required
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Email</Label>
                            <div className="relative mt-1.5">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5E636E]" />
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                    placeholder="jane@nztaxation.nz"
                                    className="pl-9"
                                    data-testid="team-email"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Role</Label>
                            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                                <SelectTrigger className="mt-1.5" data-testid="team-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="firm_staff">Staff — review documents, manage clients</SelectItem>
                                    <SelectItem value="firm_admin">Admin — full firm settings + team</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Temporary password</Label>
                            <div className="mt-1.5 flex gap-2">
                                <Input
                                    type="text"
                                    value={form.password}
                                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                                    className="font-mono"
                                    data-testid="team-password"
                                    required
                                    minLength={8}
                                />
                                <Button type="button" variant="outline" onClick={genPassword} data-testid="team-regen-pw">Regenerate</Button>
                            </div>
                            <p className="mt-1.5 text-[11px] text-[#5E636E]">Share this securely (Signal, encrypted note, in person). The new staffer will change it on first sign-in.</p>
                        </div>
                        {err && (
                            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 space-y-2" data-testid="team-error">
                                <div>{typeof err === "string" ? err : err.text}</div>
                                {typeof err === "object" && err?.canConvert && (
                                    <button
                                        type="button"
                                        onClick={(ev) => submit(ev, { convertClient: true })}
                                        disabled={busy}
                                        className="inline-flex items-center gap-1 rounded-md bg-rose-700 text-white px-2.5 py-1 text-[11px] font-semibold hover:bg-rose-800"
                                        data-testid="team-convert-client-btn"
                                    >
                                        Convert existing client login to staff
                                    </button>
                                )}
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={busy} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="team-submit">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add staff member"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!confirmRemove} onOpenChange={(v) => { if (!v) setConfirmRemove(null); }}>
                <DialogContent className="max-w-sm" data-testid="team-remove-dialog">
                    <DialogHeader>
                        <DialogTitle>Remove staff member?</DialogTitle>
                        <DialogDescription>
                            {confirmRemove ? (
                                <>
                                    <span className="font-medium text-[#111]">{confirmRemove.name || confirmRemove.email}</span> ({confirmRemove.email}) will lose access immediately. Their assigned reviews and activity history are preserved.
                                </>
                            ) : null}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setConfirmRemove(null)} disabled={!!removingId}>Cancel</Button>
                        <Button
                            type="button"
                            onClick={() => confirmRemove && removeStaff(confirmRemove)}
                            disabled={!!removingId}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid="team-remove-confirm"
                        >
                            {removingId ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ChangePasswordCard() {
    const { user } = useAuth();
    const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    async function submit(e) {
        e?.preventDefault();
        setErr(null);
        if (pw.next.length < 8) { setErr("New password must be at least 8 characters"); return; }
        if (pw.next !== pw.confirm) { setErr("New password and confirmation don't match"); return; }
        setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
            toast.success("Password updated");
            setPw({ current: "", next: "", confirm: "" });
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <div className="co-card co-card-pad lg:col-span-2" data-testid="change-password-card">
            <div className="co-label">Security</div>
            <h3 className="mt-1 font-display text-lg">Change your password</h3>
            <p className="mt-1 text-sm text-[#5E636E]">
                Signed in as <span className="font-medium text-[#0F1115]">{user?.email}</span>. Pick a strong, fresh password — minimum 8 characters.
            </p>
            <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="change-password-form">
                <div className="sm:col-span-1">
                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Current password</Label>
                    <Input
                        type="password"
                        autoComplete="current-password"
                        value={pw.current}
                        onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
                        className="mt-1.5"
                        data-testid="pw-current"
                        required
                    />
                </div>
                <div className="sm:col-span-1">
                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">New password</Label>
                    <Input
                        type="password"
                        autoComplete="new-password"
                        value={pw.next}
                        onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
                        className="mt-1.5"
                        data-testid="pw-next"
                        required
                        minLength={8}
                    />
                </div>
                <div className="sm:col-span-1">
                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Confirm new</Label>
                    <Input
                        type="password"
                        autoComplete="new-password"
                        value={pw.confirm}
                        onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                        className="mt-1.5"
                        data-testid="pw-confirm"
                        required
                        minLength={8}
                    />
                </div>
                {err && <div className="sm:col-span-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="pw-error">{err}</div>}
                <div className="sm:col-span-3">
                    <Button
                        type="submit"
                        disabled={busy || !pw.current || !pw.next || !pw.confirm}
                        className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                        data-testid="pw-submit"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <>
            <dt className="text-[#5E636E]">{k}</dt>
            <dd>{v}</dd>
        </>
    );
}


function StaffDefaultsCard() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [catalog, setCatalog] = useState([]);
    const [disabled, setDisabled] = useState(new Set());
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get("/positions/catalog").then((r) => r.data || []),
            api.get("/positions/staff-defaults").then((r) => r.data?.disabled || []),
        ]).then(([cat, d]) => { setCatalog(cat); setDisabled(new Set(d)); })
          .catch((e) => toast.error(formatApiError(e)));
    }, []);

    function toggle(k) {
        setDisabled((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
    }
    async function save() {
        setBusy(true);
        try {
            await api.put("/positions/staff-defaults", { disabled: Array.from(disabled) });
            toast.success("Staff default access saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <div className="co-card co-card-pad lg:col-span-2" data-testid="staff-defaults-card">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="co-label">Staff default access</div>
                    <h3 className="mt-1 font-display text-lg">What every staff member can do</h3>
                    <p className="mt-1 text-sm text-[#5E636E]">
                        By default staff get all capabilities (same as admin). Tick a capability below to <b>disable</b> it tenant-wide for every staff member.
                        Positions can still override individual grants.
                    </p>
                </div>
                {isAdmin && (
                    <Button onClick={save} disabled={busy} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white shrink-0" data-testid="staff-defaults-save">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                    </Button>
                )}
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {catalog.map((c) => (
                    <label key={c.key} className={`flex items-start gap-2 border border-[#E4E4E7] rounded-md p-2 ${isAdmin ? "cursor-pointer hover:bg-zinc-50" : "opacity-70"}`}>
                        <input type="checkbox" checked={disabled.has(c.key)} disabled={!isAdmin} onChange={() => toggle(c.key)} className="mt-0.5" data-testid={`staff-default-${c.key}`} />
                        <div>
                            <div className="text-[12.5px] font-medium">Disable: {c.label}</div>
                            <div className="text-[11.5px] text-[#5E636E]">{c.description}</div>
                        </div>
                    </label>
                ))}
            </div>
        </div>
    );
}

function PositionsCard() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [catalog, setCatalog] = useState([]);
    const [positions, setPositions] = useState([]);
    const [editing, setEditing] = useState(null); // { id?, name, description, capabilities: Set }
    const [busy, setBusy] = useState(false);

    async function load() {
        try {
            const [{ data: cat }, { data: pos }] = await Promise.all([
                api.get("/positions/catalog"),
                api.get("/positions"),
            ]);
            setCatalog(cat || []);
            setPositions(pos || []);
        } catch (e) { toast.error(formatApiError(e)); }
    }
    useEffect(() => { load(); }, []);

    function startNew() {
        setEditing({ id: null, name: "", description: "", capabilities: new Set() });
    }
    function startEdit(p) {
        setEditing({ id: p.id, name: p.name, description: p.description || "", capabilities: new Set(p.capabilities || []) });
    }
    function toggleCap(k) {
        setEditing((e) => {
            const next = new Set(e.capabilities);
            if (next.has(k)) next.delete(k); else next.add(k);
            return { ...e, capabilities: next };
        });
    }
    async function save() {
        if (!editing.name.trim()) { toast.error("Name required"); return; }
        setBusy(true);
        try {
            const body = { name: editing.name.trim(), description: editing.description || null, capabilities: Array.from(editing.capabilities) };
            if (editing.id) {
                await api.put(`/positions/${editing.id}`, body);
                toast.success("Position updated");
            } else {
                await api.post("/positions", body);
                toast.success("Position created");
            }
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function remove(p) {
        if (!window.confirm(`Delete "${p.name}"? Staff currently in this position will lose it.`)) return;
        try {
            await api.delete(`/positions/${p.id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div className="co-card co-card-pad lg:col-span-2" data-testid="positions-card">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="co-label">Positions &amp; permissions</div>
                    <h3 className="mt-1 font-display text-lg">Position types</h3>
                    <p className="mt-1 text-sm text-[#5E636E]">
                        Create positions like &quot;Staff 1&quot;, &quot;Reviewer&quot; or &quot;Bookkeeper&quot; with tickable capabilities.
                        Admins always have every capability. Staff keep their current access by default — positions only <b>add</b> extra permissions.
                    </p>
                </div>
                {isAdmin && (
                    <Button onClick={startNew} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white shrink-0" data-testid="position-new">
                        <Plus className="h-4 w-4" /> New position
                    </Button>
                )}
            </div>

            <ul className="mt-5 divide-y divide-[#E4E4E7] border border-[#E4E4E7] rounded-lg overflow-hidden" data-testid="positions-list">
                {positions.length === 0 && <li className="px-4 py-6 text-sm text-[#5E636E]">No positions yet.</li>}
                {positions.map((p) => (
                    <li key={p.id} className="px-4 py-3 flex items-start gap-3" data-testid={`position-row-${p.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{p.name}</div>
                            {p.description && <div className="text-xs text-[#5E636E]">{p.description}</div>}
                            <div className="mt-1 flex flex-wrap gap-1">
                                {(p.capabilities || []).length === 0 ? (
                                    <span className="text-[11px] text-[#5E636E] italic">No extra capabilities</span>
                                ) : p.capabilities.map((c) => {
                                    const found = catalog.find((x) => x.key === c);
                                    return (
                                        <span key={c} className="inline-flex items-center rounded-full bg-[#F5F4EF] text-[#0E3B2E] text-[10.5px] font-medium px-2 py-0.5">
                                            {found?.label || c}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                        {isAdmin && (
                            <div className="flex gap-1">
                                <button type="button" onClick={() => startEdit(p)} className="h-8 px-2 rounded-full text-[11.5px] text-[#5E636E] hover:bg-zinc-100" data-testid={`position-edit-${p.id}`}>Edit</button>
                                <button type="button" onClick={() => remove(p)} className="h-8 w-8 grid place-items-center rounded-full text-[#5E636E] hover:bg-rose-50 hover:text-rose-600" title="Delete">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </li>
                ))}
            </ul>

            {editing && (
                <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
                    <DialogContent className="max-w-xl" data-testid="position-edit-dialog">
                        <DialogHeader><DialogTitle>{editing.id ? "Edit position" : "New position"}</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <Label>Name</Label>
                                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="position-name" />
                            </div>
                            <div>
                                <Label>Description (optional)</Label>
                                <Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="position-desc" />
                            </div>
                            <div>
                                <Label>Capabilities</Label>
                                <div className="mt-1 space-y-1 max-h-[320px] overflow-y-auto border border-[#E4E4E7] rounded-md p-2">
                                    {catalog.map((c) => (
                                        <label key={c.key} className="flex items-start gap-2 py-1 cursor-pointer hover:bg-zinc-50 rounded px-1">
                                            <input type="checkbox" className="mt-0.5" checked={editing.capabilities.has(c.key)} onChange={() => toggleCap(c.key)} data-testid={`position-cap-${c.key}`} />
                                            <div>
                                                <div className="text-[12.5px] font-medium">{c.label}</div>
                                                <div className="text-[11.5px] text-[#5E636E]">{c.description}</div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                            <Button onClick={save} disabled={busy} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="position-save">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}


function WeeklyUsageReportCard() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [settings, setSettings] = useState(null);
    const [newEmail, setNewEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [preview, setPreview] = useState(null);

    async function load() {
        try {
            const { data } = await api.get("/admin/usage-report/settings");
            setSettings(data);
        } catch (e) {
            // Firm staff can't read settings — silently degrade.
            setSettings({ enabled: false, recipients: [] });
        }
    }
    useEffect(() => { load(); }, []);

    async function save(next) {
        if (!isAdmin) return;
        setBusy(true);
        try {
            const { data } = await api.put("/admin/usage-report/settings", next);
            setSettings(data);
            toast.success("Weekly report settings saved");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    function addRecipient() {
        const e = newEmail.trim().toLowerCase();
        if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
            toast.error("Enter a valid email");
            return;
        }
        const list = Array.from(new Set([...(settings.recipients || []), e]));
        setNewEmail("");
        save({ enabled: settings.enabled, recipients: list });
    }

    function removeRecipient(email) {
        const list = (settings.recipients || []).filter((r) => r !== email);
        save({ enabled: settings.enabled, recipients: list });
    }

    function toggleEnabled(enabled) {
        save({ enabled, recipients: settings.recipients || [] });
    }

    async function openPreview() {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/usage-report/preview");
            setPreview(data);
            setPreviewOpen(true);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function sendNow() {
        if (!isAdmin) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/usage-report/send-now");
            if (data.status === "sent") {
                const ok = (data.outcomes || []).filter((o) => o.status === "sent").length;
                toast.success(`Weekly report sent to ${ok} recipient${ok === 1 ? "" : "s"}`);
            } else if (data.status === "skipped") {
                toast.info(`Skipped: ${data.reason.replace(/_/g, " ")}`);
            } else {
                toast.error(`Send failed: ${data.reason || "unknown"}`);
            }
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    if (!settings) {
        return (
            <div className="co-card co-card-pad lg:col-span-2" data-testid="weekly-report-card">
                <div className="text-sm text-[#5E636E]">Loading…</div>
            </div>
        );
    }

    return (
        <div className="co-card co-card-pad lg:col-span-2" data-testid="weekly-report-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="co-label flex items-center gap-1.5">
                        <CalendarClock className="h-3.5 w-3.5" /> Weekly usage report
                    </div>
                    <h3 className="mt-1 font-display text-lg">Who&apos;s using ClientOps?</h3>
                    <p className="mt-1 text-sm text-[#5E636E] max-w-2xl">
                        Every Monday morning (NZ time) we email a snapshot of which portal clients logged in
                        and what they did in the last 7 days — plus who hasn&apos;t touched the portal yet.
                    </p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm shrink-0" data-testid="weekly-report-enabled-toggle">
                    <input
                        type="checkbox"
                        checked={!!settings.enabled}
                        onChange={(e) => toggleEnabled(e.target.checked)}
                        disabled={!isAdmin || busy}
                        className="h-4 w-4 accent-emerald-600"
                    />
                    <span className={settings.enabled ? "text-emerald-700 font-medium" : "text-[#5E636E]"}>
                        {settings.enabled ? "Enabled" : "Disabled"}
                    </span>
                </label>
            </div>

            <div className="mt-4">
                <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Recipients</Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="weekly-report-recipients">
                    {(settings.recipients || []).map((r) => (
                        <span
                            key={r}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-[#FAFAFA] px-2.5 py-1 text-xs font-mono"
                            data-testid={`weekly-report-recipient-${r}`}
                        >
                            <Mail className="h-3 w-3 text-[#5E636E]" />
                            {r}
                            {isAdmin && (
                                <button
                                    onClick={() => removeRecipient(r)}
                                    className="ml-0.5 rounded-full hover:bg-zinc-200 p-0.5"
                                    aria-label={`Remove ${r}`}
                                    disabled={busy}
                                >
                                    <XIcon className="h-3 w-3" />
                                </button>
                            )}
                        </span>
                    ))}
                    {(settings.recipients || []).length === 0 && (
                        <span className="text-xs text-[#5E636E] italic">No recipients yet — add one below.</span>
                    )}
                </div>
                {isAdmin && (
                    <div className="mt-3 flex gap-2 max-w-md">
                        <Input
                            type="email"
                            placeholder="name@firm.co.nz"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                            disabled={busy}
                            data-testid="weekly-report-new-email"
                        />
                        <Button variant="outline" onClick={addRecipient} disabled={busy || !newEmail} data-testid="weekly-report-add-recipient">
                            <Plus className="h-4 w-4" /> Add
                        </Button>
                    </div>
                )}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2 pt-4 border-t border-[#E4E4E7]">
                <Button variant="outline" onClick={openPreview} disabled={busy} data-testid="weekly-report-preview-btn">
                    <Eye className="h-4 w-4" /> Preview this week&apos;s report
                </Button>
                {isAdmin && (
                    <Button
                        onClick={sendNow}
                        disabled={busy || !settings.enabled || (settings.recipients || []).length === 0}
                        className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                        title={!settings.enabled ? "Enable the report first" : "Send now (bypasses the Monday schedule)"}
                        data-testid="weekly-report-send-now-btn"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send now</>}
                    </Button>
                )}
                {settings.last_sent_at && (
                    <span className="text-xs text-[#5E636E] ml-1">
                        Last sent {new Date(settings.last_sent_at).toLocaleString("en-NZ")}
                    </span>
                )}
            </div>

            <Dialog open={previewOpen} onOpenChange={(v) => { setPreviewOpen(v); if (!v) setPreview(null); }}>
                <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col" data-testid="weekly-report-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>Preview weekly usage report</DialogTitle>
                        <DialogDescription>
                            This is exactly what your recipients will receive on Monday morning.
                        </DialogDescription>
                    </DialogHeader>
                    {preview && (
                        <div className="flex-1 overflow-hidden flex flex-col gap-2 min-h-0">
                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                                <span className="text-[#5E636E]">Subject</span><span className="font-medium truncate">{preview.subject}</span>
                            </div>
                            <iframe
                                title="Weekly report preview"
                                srcDoc={preview.html}
                                sandbox=""
                                className="flex-1 min-h-[520px] w-full rounded-md border border-[#E4E4E7] bg-white"
                                data-testid="weekly-report-preview-iframe"
                            />
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
